/**
 * Event Tracking Service
 * 
 * Simple Firestore-based event logging for conversion funnel analysis.
 * Events are stored in: users/{uid}/events/{eventId}
 * 
 * Key events for monetization:
 * - lead.created: User signed up
 * - address.captured: Shipping address added
 * - sample.requested: User requested sample
 * - recommendation.viewed: Editor picks displayed
 * - recommendation.clicked: User clicked product link
 * - editor_pick.clicked: Specific to paid placements
 */

import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from './firebaseConfig';
import type { AffiliateSource } from '../types/product';
import type { SupportedRegion } from '../config/affiliateConfig';
import { getRegionConfig } from '../config/affiliateConfig';
import { getApiBaseUrl } from '../utils/apiConfig';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Base event data stored in Firestore
 */
export interface EventData {
  type: string;
  timestamp: ReturnType<typeof Timestamp.now>;
  timestampISO: string;
  metadata: Record<string, unknown>;
  userAgent: string;
  url: string;
}

/**
 * Address information for address.captured events
 */
export interface AddressMetadata {
  address1?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  [key: string]: unknown;
}

/**
 * Product information for recommendation tracking
 */
export interface ProductMetadata {
  id?: string;
  name?: string;
  title?: string;
  brand?: string;
  price?: number;
  isPaidPlacement?: boolean;
  placementTier?: string | number;
  confidence?: number;
  url?: string;
  [key: string]: unknown;
}

/**
 * Editor picks array item
 */
export interface EditorPick {
  id?: string;
  isPaidPlacement?: boolean;
  confidence?: number;
  // Affiliate tracking fields
  affiliateUrl?: string;
  affiliateSource?: AffiliateSource;
  trackingId?: string;
  cpcBid?: number;
  revenueShare?: number;
  [key: string]: unknown;
}

/**
 * Feedback submission data
 */
export interface FeedbackMetadata {
  productId?: string;
  rating?: number;
  willPurchase?: boolean;
  sideEffects?: string;
  [key: string]: unknown;
}

/**
 * Order data for tracking
 */
export interface OrderMetadata {
  totalCents?: number;
  items?: Array<{ productId?: string; [key: string]: unknown }>;
  [key: string]: unknown;
}

/**
 * Subscription data for tracking
 */
export interface SubscriptionMetadata {
  plan?: string;
  monthlyValueCents?: number;
  [key: string]: unknown;
}

// ============================================================================
// GA4 Integration
// ============================================================================

/** Send event to Google Analytics 4 if gtag is available */
function sendToGA4(eventType: string, metadata: Record<string, unknown>) {
  if (typeof window !== 'undefined' && typeof (window as unknown as Record<string, unknown>).gtag === 'function') {
    const gtag = (window as unknown as Record<string, unknown>).gtag as (...args: unknown[]) => void;
    // Map internal event types to GA4 recommended events
    const ga4EventMap: Record<string, string> = {
      'lead.created': 'sign_up',
      'recommendation.viewed': 'view_item_list',
      'recommendation.clicked': 'select_item',
      'affiliate.clicked': 'select_item',
      'editor_pick.clicked': 'select_item',
      'order.placed': 'purchase',
      'sample.requested': 'add_to_cart',
      'feedback.submitted': 'submit_feedback',
      'address.captured': 'add_shipping_info',
    };
    const ga4Event = ga4EventMap[eventType] || eventType.replace(/\./g, '_');
    // Allowlist safe GA4 params to prevent PII leakage
    const safeKeys = new Set([
      'event_category', 'event_label', 'productId', 'productName',
      'source', 'placement', 'query', 'resultCount', 'plan',
      'value', 'currency', 'item_id', 'item_name',
    ]);
    const safeParams: Record<string, unknown> = {
      event_category: eventType.split('.')[0],
      event_label: eventType,
    };
    for (const [key, val] of Object.entries(metadata)) {
      if (
        safeKeys.has(key) &&
        val !== null &&
        val !== undefined &&
        (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean')
      ) {
        safeParams[key] = val;
      }
    }
    gtag('event', ga4Event, safeParams);
  }
}

// ============================================================================
// Core Event Logging
// ============================================================================

/**
 * Log an event to Firestore
 * @param uid - User ID
 * @param eventType - Event type (e.g., 'sample.requested')
 * @param metadata - Additional event data
 * @returns Event document ID or null if failed
 */
export async function logEvent(
  uid: string,
  eventType: string,
  metadata: Record<string, unknown> = {}
): Promise<string | null> {
  if (!uid) {
    console.warn('⚠️ Cannot log event: missing uid');
    return null;
  }

  try {
    // Filter out undefined values — Firestore rejects them
    const cleanMetadata = Object.fromEntries(
      Object.entries(metadata || {}).filter(([, v]) => v !== undefined)
    );

    const eventData: EventData = {
      type: eventType,
      timestamp: Timestamp.now(),
      timestampISO: new Date().toISOString(),
      metadata: cleanMetadata,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'server',
      url: typeof window !== 'undefined' ? window.location.href : '',
    };

    // Phase 1 Step 4: write to masterProfiles events subcollection
    const eventsRef = collection(db, 'masterProfiles', uid, 'events');
    const docRef = await addDoc(eventsRef, eventData);

    console.log(`📊 Event logged: ${eventType}`, {
      eventId: docRef.id,
      metadata,
    });

    // Also send to GA4 for external analytics
    sendToGA4(eventType, cleanMetadata);

    return docRef.id;
  } catch (error) {
    console.error('❌ Failed to log event:', eventType, error);
    return null;
  }
}

// ============================================================================
// Specific Event Tracking Functions
// ============================================================================

/**
 * Track lead creation (user signup)
 * @param uid - User ID
 * @param metadata - Additional metadata
 */
export function trackLeadCreated(
  uid: string,
  metadata: Record<string, unknown> = {}
): Promise<string | null> {
  return logEvent(uid, 'lead.created', {
    source: metadata.source || 'google-oauth',
    ...metadata,
  });
}

/**
 * Track address capture (for sample delivery)
 * @param uid - User ID
 * @param address - Address information
 */
export function trackAddressCaptured(
  uid: string,
  address: AddressMetadata = {}
): Promise<string | null> {
  return logEvent(uid, 'address.captured', {
    hasAddress1: !!address.address1,
    hasCity: !!address.city,
    hasPostalCode: !!address.postalCode,
    country: address.country,
  });
}

/**
 * Track sample request submission
 * @param uid - User ID
 * @param metadata - Request metadata including profile completeness
 */
export function trackSampleRequested(
  uid: string,
  metadata: Record<string, unknown> = {}
): Promise<string | null> {
  return logEvent(uid, 'sample.requested', {
    profileCompleteness: metadata.profileCompleteness,
    ...metadata,
  });
}

/**
 * Track editor picks view
 * @param uid - User ID
 * @param picks - Array of editor picks shown
 */
export function trackRecommendationViewed(
  uid: string,
  picks: EditorPick[] = []
): Promise<string | null> {
  return logEvent(uid, 'recommendation.viewed', {
    count: picks.length,
    productIds: picks.map(p => p.id),
    paidCount: picks.filter(p => p.isPaidPlacement).length,
    averageConfidence: picks.length > 0
      ? picks.reduce((sum, p) => sum + (p.confidence || 0), 0) / picks.length
      : 0,
  });
}

/**
 * Track product link click (conversion tracking)
 * @param uid - User ID
 * @param product - Product information
 */
export function trackRecommendationClicked(
  uid: string,
  product: ProductMetadata = {}
): Promise<string | null> {
  return logEvent(uid, 'recommendation.clicked', {
    productId: product.id,
    productName: product.name,
    brand: product.brand,
    price: product.price,
    isPaidPlacement: product.isPaidPlacement,
    placementTier: product.placementTier,
    confidence: product.confidence,
    url: product.url,
  });
}

/**
 * Track editor pick click specifically (for paid placement analytics)
 * @param uid - User ID
 * @param product - Product information
 */
export function trackEditorPickClicked(
  uid: string,
  product: ProductMetadata = {}
): Promise<string | null> {
  return logEvent(uid, 'editor_pick.clicked', {
    productId: product.id,
    productName: product.name,
    brand: product.brand,
    price: product.price,
    isPaidPlacement: product.isPaidPlacement,
    placementTier: product.placementTier,
    confidence: product.confidence,
    url: product.url,
  });
}

/**
 * Track feedback submission
 * @param uid - User ID
 * @param feedback - Feedback data
 */
export function trackFeedbackSubmitted(
  uid: string,
  feedback: FeedbackMetadata = {}
): Promise<string | null> {
  return logEvent(uid, 'feedback.submitted', {
    productId: feedback.productId,
    rating: feedback.rating,
    willPurchase: feedback.willPurchase,
    hasSideEffects: !!feedback.sideEffects,
  });
}

/**
 * Track order placement (future)
 * @param uid - User ID
 * @param order - Order information
 */
export function trackOrderPlaced(
  uid: string,
  order: OrderMetadata = {}
): Promise<string | null> {
  return logEvent(uid, 'order.placed', {
    orderValue: order.totalCents,
    itemCount: order.items?.length,
    productIds: order.items?.map(i => i.productId),
  });
}

/**
 * Track subscription (future)
 * @param uid - User ID
 * @param subscription - Subscription information
 */
export function trackPouchSubscribed(
  uid: string,
  subscription: SubscriptionMetadata = {}
): Promise<string | null> {
  return logEvent(uid, 'pouch.subscribed', {
    plan: subscription.plan,
    monthlyValue: subscription.monthlyValueCents,
  });
}

// ============================================================================
// Affiliate Click Tracking (Geo-Routing Phase 4)
// ============================================================================

export interface AffiliateClickMetadata {
  productId?: string;
  productName?: string;
  brand?: string;
  price?: number;
  url?: string;
  affiliateSource?: AffiliateSource;
  region?: SupportedRegion;
  marketplace?: string;
  isPaidPlacement?: boolean;
  placementRow?: 'ai-picks' | 'web-search' | 'sponsored';
  [key: string]: unknown;
}

/**
 * Derive the marketplace name from affiliate source and region.
 * Uses the region's Amazon domain for Amazon links; network name for others.
 */
function resolveMarketplace(
  affiliateSource?: AffiliateSource,
  region?: SupportedRegion,
  explicitMarketplace?: string
): string {
  if (explicitMarketplace) return explicitMarketplace;
  if (!affiliateSource) return 'unknown';
  if (affiliateSource === 'amazon') {
    const cfg = region ? getRegionConfig(region) : undefined;
    return cfg?.amazon?.domain ?? 'amazon.com';
  }
  return affiliateSource; // 'coupang', 'iherb', 'yesstyle', etc.
}

/**
 * Track an affiliate product link click with region and marketplace data.
 * Used for geo-routing analytics (click-through per region per marketplace).
 *
 * Also fires POST /api/trackClick so the admin dashboard Affiliate tab
 * (which reads from the `clickEvents` collection) shows accurate totals.
 */
export async function trackAffiliateClick(
  uid: string,
  metadata: AffiliateClickMetadata = {}
): Promise<string | null> {
  // Fire backend trackClick so admin dashboard sees it
  trackClickEvent({
    productId: metadata.productId || 'unknown',
    placement: mapPlacementRow(metadata.placementRow),
    slotIndex: 0,
    isPaidPlacement: metadata.isPaidPlacement ?? false,
    affiliateSource: metadata.affiliateSource,
    affiliateUrl: metadata.url,
    userId: uid,
  }).catch(() => {}); // fire-and-forget, don't block UX

  return logEvent(uid, 'affiliate.clicked', {
    productId: metadata.productId,
    productName: metadata.productName,
    brand: metadata.brand,
    price: metadata.price,
    url: metadata.url,
    affiliateSource: metadata.affiliateSource,
    region: metadata.region,
    marketplace: resolveMarketplace(metadata.affiliateSource, metadata.region, metadata.marketplace),
    isPaidPlacement: metadata.isPaidPlacement,
    placementRow: metadata.placementRow,
  });
}

/** Map client placement names to the trackClick API's union type */
function mapPlacementRow(
  row?: 'ai-picks' | 'web-search' | 'sponsored'
): 'editorPicks_panel' | 'sponsored_row' | 'chat_pill' {
  switch (row) {
    case 'ai-picks': return 'editorPicks_panel';
    case 'sponsored': return 'sponsored_row';
    case 'web-search': return 'chat_pill';
    default: return 'editorPicks_panel';
  }
}

/**
 * Call POST /api/trackClick to record click in `clickEvents` collection.
 * This powers the admin dashboard's Affiliate analytics tab.
 */
export async function trackClickEvent(params: {
  productId: string;
  slotId?: string;
  placement: 'editorPicks_panel' | 'editorPicks_peekBar' | 'editorPicks_bottomSheet' | 'chat_pill' | 'sponsored_row';
  slotIndex: number;
  isPaidPlacement: boolean;
  affiliateSource?: AffiliateSource;
  affiliateUrl?: string;
  userId?: string;
}): Promise<void> {
  try {
    await fetch(`${getApiBaseUrl()}/trackClick`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
  } catch {
    // Swallow — click tracking must never block UX
  }
}

// ============================================================================
// Default Export for Backward Compatibility
// ============================================================================

const eventTrackingService = {
  logEvent,
  trackLeadCreated,
  trackAddressCaptured,
  trackSampleRequested,
  trackRecommendationViewed,
  trackRecommendationClicked,
  trackEditorPickClicked,
  trackFeedbackSubmitted,
  trackOrderPlaced,
  trackPouchSubscribed,
  trackAffiliateClick,
  trackClickEvent,
};

export default eventTrackingService;
